import type React from "react";
import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { formatDate } from "../../lib/utils";
import { Input } from "../ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Search, Calendar, Package, Check, Download } from "lucide-react";
import { DateInput } from "../ui/dateInput";
import Pagination from "../ui/pagination";
import { useToast } from "../../hooks/use-toast";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { useAuth } from "../../lib/authContext";

interface RejectedOrder {
	id: number;
	invoice_number: string;
	created_at: string;
	status: string;
	entity_name: string;
	// Item-level fields
	product_id: string;
	product_metadata: {
		name: string;
		[key: string]: any;
	};
	reject_reason: string;
	pincode: string;
}

interface RejectedOrdersResponse {
	success: boolean;
	data: RejectedOrder[];
	pagination: {
		page: number;
		limit: number;
		total: number;
		totalPages: number;
	};
	message: string;
}

interface MissingProductStock {
	id: number;
	product_id: string;
	requested_quantity: number;
	created_at: string;
	request_made_at: string;
	is_active: boolean;
	product_name: string | null;
	tp_code: string | null;
	current_stock: number | null;
	is_auto_stocked: boolean | null;
	pincode: string;
}

interface MissingProductStockResponse {
	success: boolean;
	data: MissingProductStock[];
	pagination: {
		page: number;
		limit: number;
		total: number;
		totalPages: number;
	};
}

interface TabData {
	orders: RejectedOrder[];
	currentPage: number;
	totalPages: number;
	totalItems: number;
	searchKeyword: string;
	startDate: string;
	endDate: string;
	limit: number;
	isLoading: boolean;
}

interface LiveInventoryData {
	items: MissingProductStock[];
	currentPage: number;
	totalPages: number;
	totalItems: number;
	searchKeyword: string;
	startDate: string;
	endDate: string;
	limit: number;
	isLoading: boolean;
}

export const RejectedOrders: React.FC = () => {
	const { toast } = useToast();
	const { excelPermissions } = useAuth();
	const [activeTab, setActiveTab] = useState<string>("orders");
	const [processingAutoStock, setProcessingAutoStock] = useState<string | null>(
		null,
	);

	// Orders tab data
	const [ordersData, setOrdersData] = useState<TabData>({
		orders: [],
		currentPage: 1,
		totalPages: 1,
		totalItems: 0,
		searchKeyword: "",
		startDate: new Date().toISOString().split("T")[0],
		endDate: new Date().toISOString().split("T")[0],
		limit: 10,
		isLoading: false,
	});

	// Local state for debounced search
	const [searchTerm, setSearchTerm] = useState("");

	// Local state for debounced date filters
	const [localStartDate, setLocalStartDate] = useState(new Date().toISOString().split("T")[0]);
	const [localEndDate, setLocalEndDate] = useState(new Date().toISOString().split("T")[0]);

	// Debounce search term
	useEffect(() => {
		const timer = setTimeout(() => {
			setOrdersData(prev => ({
				...prev,
				searchKeyword: searchTerm,
				currentPage: 1,
			}));
		}, 1000);

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Debounce date filters
	useEffect(() => {
		const timer = setTimeout(() => {
			setOrdersData(prev => ({
				...prev,
				startDate: localStartDate,
				endDate: localEndDate,
				currentPage: 1,
			}));
		}, 1000);

		return () => clearTimeout(timer);
	}, [localStartDate, localEndDate]);

	// Live Inventory tab data
	const [liveInventoryData, setLiveInventoryData] = useState<LiveInventoryData>(
		{
			items: [],
			currentPage: 1,
			totalPages: 1,
			totalItems: 0,
			searchKeyword: "",
			startDate: new Date().toISOString().split("T")[0],
			endDate: new Date().toISOString().split("T")[0],
			limit: 10,
			isLoading: false,
		},
	);

	const fetchRejectedOrders = useCallback(async () => {
		try {
			setOrdersData(prev => ({ ...prev, isLoading: true }));

			const result = (await api.getRejectedOrders({
				page: ordersData.currentPage,
				limit: ordersData.limit,
				keyword: ordersData.searchKeyword.trim(),
				start_date: ordersData.startDate,
				end_date: ordersData.endDate,
			})) as RejectedOrdersResponse;

			if (result?.success) {
				setOrdersData(prev => ({
					...prev,
					orders: result.data || [],
					totalPages: result.pagination?.totalPages || 1,
					totalItems: result.pagination?.total || 0,
					isLoading: false,
				}));
			} else {
				toast({
					title: "Error",
					description: "Failed to fetch rejected orders",
					variant: "destructive",
				});
			}
		} catch (error) {
			console.error("Failed to fetch rejected orders:", error);
			toast({
				title: "Error",
				description: "Failed to fetch rejected orders",
				variant: "destructive",
			});
		} finally {
			setOrdersData(prev => ({ ...prev, isLoading: false }));
		}
	}, [
		ordersData.currentPage,
		ordersData.limit,
		ordersData.searchKeyword,
		ordersData.startDate,
		ordersData.endDate,
		toast,
	]);

	const fetchMissingProductStock = useCallback(async () => {
		try {
			setLiveInventoryData(prev => ({ ...prev, isLoading: true }));

			const response = (await api.getMissingProductStock(
				liveInventoryData.currentPage,
				liveInventoryData.limit,
				liveInventoryData.searchKeyword,
				liveInventoryData.startDate,
				liveInventoryData.endDate,
			)) as MissingProductStockResponse;

			if (!response.success) {
				throw new Error("Failed to fetch missing product stock");
			}

			const result: MissingProductStockResponse = response;

			if (result?.success) {
				setLiveInventoryData(prev => ({
					...prev,
					items: result.data || [],
					totalPages: result.pagination?.totalPages || 1,
					totalItems: result.pagination?.total || 0,
					isLoading: false,
				}));
			} else {
				toast({
					title: "Error",
					description: "Failed to fetch missing product stock",
					variant: "destructive",
				});
			}
		} catch (error) {
			console.error("Failed to fetch missing product stock:", error);
			toast({
				title: "Error",
				description: "Failed to fetch missing product stock",
				variant: "destructive",
			});
		} finally {
			setLiveInventoryData(prev => ({ ...prev, isLoading: false }));
		}
	}, [
		liveInventoryData.currentPage,
		liveInventoryData.limit,
		liveInventoryData.searchKeyword,
		liveInventoryData.startDate,
		liveInventoryData.endDate,
		toast,
	]);

	// Fetch data when tab changes or filters change
	useEffect(() => {
		if (activeTab === "orders") {
			fetchRejectedOrders();
		} else if (activeTab === "live-inventory") {
			fetchMissingProductStock();
		}
	}, [activeTab, fetchRejectedOrders, fetchMissingProductStock]);

	const handleOrdersSearch = () => {
		setOrdersData(prev => ({ ...prev, currentPage: 1 }));
		fetchRejectedOrders();
	};

	const handleLiveInventorySearch = () => {
		setLiveInventoryData(prev => ({ ...prev, currentPage: 1 }));
		fetchMissingProductStock();
	};

	const handleDownloadOrdersExcel = async () => {
		try {
			const response = await api.downloadRejectedOrdersExcel({
				keyword: ordersData.searchKeyword.trim(),
				start_date: ordersData.startDate,
				end_date: ordersData.endDate,
			});

			if (response.success) {
				const link = document.createElement("a");
				link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data}`;
				link.download = response.filename;
				link.click();
				toast({
					title: "Success",
					description: "Excel file downloaded successfully",
				});
			} else {
				toast({
					title: "Error",
					description: response.error || "Failed to download Excel file",
					variant: "destructive",
				});
			}
		} catch (error) {
			console.error("Failed to download Excel:", error);
			toast({
				title: "Error",
				description: "Failed to download Excel file",
				variant: "destructive",
			});
		}
	};

	const handleDownloadLiveInventoryExcel = async () => {
		try {
			const response = await api.downloadMissingProductStockExcel({
				keyword: liveInventoryData.searchKeyword.trim(),
				start_date: liveInventoryData.startDate,
				end_date: liveInventoryData.endDate,
			});

			if (response.success) {
				const link = document.createElement("a");
				link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data}`;
				link.download = response.filename;
				link.click();
				toast({
					title: "Success",
					description: "Excel file downloaded successfully",
				});
			} else {
				toast({
					title: "Error",
					description: response.error || "Failed to download Excel file",
					variant: "destructive",
				});
			}
		} catch (error) {
			console.error("Failed to download Excel:", error);
			toast({
				title: "Error",
				description: "Failed to download Excel file",
				variant: "destructive",
			});
		}
	};

	const handleMarkAsAutoStock = async (productId: string) => {
		try {
			setProcessingAutoStock(productId);

			const response = await api.markAsAutoStock(productId);

			if (response.success) {
				toast({
					title: "Success",
					description: "Product marked as auto stock successfully",
				});
				// Refresh the live inventory data
				fetchMissingProductStock();
			} else {
				toast({
					title: "Error",
					description: response.error || "Failed to mark product as auto stock",
					variant: "destructive",
				});
			}
		} catch (error) {
			console.error("Failed to mark product as auto stock:", error);
			toast({
				title: "Error",
				description: "Failed to mark product as auto stock",
				variant: "destructive",
			});
		} finally {
			setProcessingAutoStock(null);
		}
	};

	const handleKeyPress = (
		e: React.KeyboardEvent,
		tab: "orders" | "live-inventory",
	) => {
		if (e.key === "Enter") {
			if (tab === "orders") {
				handleOrdersSearch();
			} else {
				handleLiveInventorySearch();
			}
		}
	};

	const handleDateChange = (
		date: string,
		field: "startDate" | "endDate",
		tab: "orders" | "live-inventory",
	) => {
		if (tab === "orders") {
			if (field === "startDate") {
				// If start date is after end date, update end date to match start date
				if (new Date(date) > new Date(localEndDate)) {
					setLocalStartDate(date);
					setLocalEndDate(date);
				} else {
					setLocalStartDate(date);
				}
			} else {
				// If end date is before start date, show error
				if (new Date(date) < new Date(localStartDate)) {
					toast({
						title: "Invalid Date Range",
						description: "End date cannot be before start date",
						variant: "destructive",
					});
					return;
				}
				setLocalEndDate(date);
			}
		} else {
			if (field === "startDate") {
				// If start date is after end date, update end date to match start date
				if (new Date(date) > new Date(liveInventoryData.endDate)) {
					setLiveInventoryData(prev => ({
						...prev,
						startDate: date,
						endDate: date,
						currentPage: 1,
					}));
				} else {
					setLiveInventoryData(prev => ({
						...prev,
						startDate: date,
						currentPage: 1,
					}));
				}
			} else {
				// If end date is before start date, show error
				if (new Date(date) < new Date(liveInventoryData.startDate)) {
					toast({
						title: "Invalid Date Range",
						description: "End date cannot be before start date",
						variant: "destructive",
					});
					return;
				}
				setLiveInventoryData(prev => ({
					...prev,
					endDate: date,
					currentPage: 1,
				}));
			}
		}
	};

	return (
		<div className="w-full p-6 bg-white dark:bg-gray-900">
			<div className="flex justify-between items-center mb-6">
				<div>
					<h1 className="text-3xl font-bold dark:text-white">Rejected SKUs</h1>
					<p className="text-gray-600 dark:text-gray-400">
						Manage rejected orders and live inventory stock issues
					</p>
				</div>
			</div>

			<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
				<TabsList className="grid w-full grid-cols-2">
					<TabsTrigger value="orders" className="flex items-center gap-2">
						<Package className="w-4 h-4" />
						Orders ({ordersData.totalItems})
					</TabsTrigger>
					<TabsTrigger
						value="live-inventory"
						className="flex items-center gap-2"
					>
						<Search className="w-4 h-4" />
						Live Inventory ({liveInventoryData.totalItems})
					</TabsTrigger>
				</TabsList>

				{/* Orders Tab */}
				<TabsContent value="orders" className="space-y-4">
					<div className="flex justify-between items-center">
						<div className="text-gray-600 dark:text-gray-400">
							View rejected items from {localStartDate} to{" "}
							{localEndDate} ({ordersData.totalItems} items total)
						</div>
						{excelPermissions.canDownloadExcel && (
							<Button
								onClick={handleDownloadOrdersExcel}
								variant="outline"
								className="flex items-center gap-2"
							>
								<Download className="w-4 h-4" />
								Download Excel
							</Button>
						)}
					</div>

					{/* Filters Section */}
					<div className="space-y-4">
						<p className="text-sm text-red-600 dark:text-red-400">
							Please select a date range less than 90 days
						</p>
						{/* Date Range Filter */}
						<div className="flex gap-4 items-center">
							<div className="flex items-center gap-2">
								<Calendar className="h-4 w-4 text-gray-500" />
								<label className="text-sm font-medium text-gray-700 dark:text-gray-300">
									Date Range:
								</label>
							</div>
							<div className="flex gap-2 items-center">
								<DateInput
									value={localStartDate}
									onChange={date =>
										handleDateChange(date, "startDate", "orders")
									}
								/>
								<span className="text-gray-500">to</span>
								<DateInput
									value={localEndDate}
									onChange={date => handleDateChange(date, "endDate", "orders")}
								/>
							</div>
						</div>

						{/* Search Filter */}
						<div className="flex gap-2 items-center">
							<div className="relative flex-1 max-w-md">
								<Input
									placeholder="Search by order ID, product ID, or rejected reason..."
									value={searchTerm}
									onChange={e => setSearchTerm(e.target.value)}
									onKeyPress={e => handleKeyPress(e, "orders")}
									className="pr-10"
								/>
								<Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
							</div>
							<Button
								onClick={handleOrdersSearch}
								disabled={ordersData.isLoading}
								className="px-6"
							>
								{ordersData.isLoading ? "Searching..." : "Search"}
							</Button>
						</div>
					</div>

					{/* Pagination Size Selector */}
					<div className="flex justify-between items-center">
						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-700 dark:text-gray-300">
								Show:
							</span>
							<Select
								value={ordersData.limit.toString()}
								onValueChange={value =>
									setOrdersData(prev => ({
										...prev,
										limit: Number(value),
										currentPage: 1,
									}))
								}
							>
								<SelectTrigger className="w-20">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="10">10</SelectItem>
									<SelectItem value="25">25</SelectItem>
									<SelectItem value="50">50</SelectItem>
									<SelectItem value="100">100</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{ordersData.totalItems > 0 && (
							<div className="text-sm text-gray-600 dark:text-gray-400">
								Showing{" "}
								{Math.min(
									(ordersData.currentPage - 1) * ordersData.limit + 1,
									ordersData.totalItems,
								)}{" "}
								to{" "}
								{Math.min(
									ordersData.currentPage * ordersData.limit,
									ordersData.totalItems,
								)}{" "}
								of {ordersData.totalItems} results
							</div>
						)}
					</div>

					{/* Orders Table */}
					<div className="rounded-md border">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="dark:text-gray-300">
										Product ID
									</TableHead>
									<TableHead className="dark:text-gray-300">SKU Name</TableHead>
									<TableHead className="dark:text-gray-300">Order ID</TableHead>
									<TableHead className="dark:text-gray-300">
										Placed On
									</TableHead>
									<TableHead className="dark:text-gray-300">Partner</TableHead>
									<TableHead className="dark:text-gray-300">Pincode</TableHead>
									<TableHead className="dark:text-gray-300">Status</TableHead>
									<TableHead className="dark:text-gray-300">
										Rejected Reason
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{ordersData.isLoading ? (
									<TableRow>
										<TableCell
											colSpan={8}
											className="text-center dark:text-gray-400 py-8"
										>
											Loading rejected orders...
										</TableCell>
									</TableRow>
								) : ordersData.orders.length > 0 ? (
									ordersData.orders.map(order => (
										<TableRow
											key={order.id}
											className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
										>
											<TableCell className="dark:text-gray-300">
												<span className="text-sm text-gray-700 dark:text-gray-300">
													{order.product_id}
												</span>
											</TableCell>
											<TableCell className="dark:text-gray-300 max-w-xs">
												<span
													className="text-sm text-gray-700 dark:text-gray-300 block truncate"
													title={order.product_metadata?.name || "N/A"}
												>
													{order.product_metadata?.name || "N/A"}
												</span>
											</TableCell>
											<TableCell className="font-medium dark:text-white">
												{order.invoice_number}
											</TableCell>
											<TableCell className="dark:text-gray-300">
												{formatDate(new Date(order.created_at))}
											</TableCell>
											<TableCell className="dark:text-gray-300">
												{order.entity_name}
											</TableCell>
											<TableCell className="dark:text-gray-300">
												{order.pincode || "N/A"}
											</TableCell>
											<TableCell>
												<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
													{order.status}
												</span>
											</TableCell>
											<TableCell className="dark:text-gray-300 max-w-xs">
												<span
													className="text-sm text-gray-700 dark:text-gray-300 block truncate"
													title={order.reject_reason || "N/A"}
												>
													{order.reject_reason || "N/A"}
												</span>
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell
											colSpan={8}
											className="text-center dark:text-gray-400 py-8"
										>
											No rejected items found
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>

					{/* Pagination */}
					{ordersData.totalPages > 1 && (
						<div className="flex justify-center">
							<Pagination
								page={ordersData.currentPage}
								totalPages={ordersData.totalPages}
								onPageChange={page =>
									setOrdersData(prev => ({ ...prev, currentPage: page }))
								}
							/>
						</div>
					)}
				</TabsContent>

				{/* Live Inventory Tab */}
				<TabsContent value="live-inventory" className="space-y-4">
					<div className="flex justify-between items-center">
						<div className="text-gray-600 dark:text-gray-400">
							Missing stock requests from {liveInventoryData.startDate} to{" "}
							{liveInventoryData.endDate} ({liveInventoryData.totalItems} items
							total)
						</div>
						<Button
							onClick={handleDownloadLiveInventoryExcel}
							variant="outline"
							className="flex items-center gap-2"
						>
							<Download className="w-4 h-4" />
							Download Excel
						</Button>
					</div>

					{/* Filters Section */}
					<div className="space-y-4">
						<p className="text-sm text-red-600 dark:text-red-400">
							Please select a date range less than 90 days
						</p>
						{/* Date Range Filter */}
						<div className="flex gap-4 items-center">
							<div className="flex items-center gap-2">
								<Calendar className="h-4 w-4 text-gray-500" />
								<label className="text-sm font-medium text-gray-700 dark:text-gray-300">
									Date Range:
								</label>
							</div>
							<div className="flex gap-2 items-center">
								<DateInput
									value={liveInventoryData.startDate}
									onChange={date =>
										handleDateChange(date, "startDate", "live-inventory")
									}
								/>
								<span className="text-gray-500">to</span>
								<DateInput
									value={liveInventoryData.endDate}
									onChange={date =>
										handleDateChange(date, "endDate", "live-inventory")
									}
								/>
							</div>
						</div>

						{/* Search Filter */}
						<div className="flex gap-2 items-center">
							<div className="relative flex-1 max-w-md">
								<Input
									placeholder="Search by product ID..."
									value={liveInventoryData.searchKeyword}
									onChange={e =>
										setLiveInventoryData(prev => ({
											...prev,
											searchKeyword: e.target.value,
										}))
									}
									onKeyPress={e => handleKeyPress(e, "live-inventory")}
									className="pr-10"
								/>
								<Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
							</div>
							<Button
								onClick={handleLiveInventorySearch}
								disabled={liveInventoryData.isLoading}
								className="px-6"
							>
								{liveInventoryData.isLoading ? "Searching..." : "Search"}
							</Button>
						</div>
					</div>

					{/* Pagination Size Selector */}
					<div className="flex justify-between items-center">
						<div className="flex items-center gap-2">
							<span className="text-sm text-gray-700 dark:text-gray-300">
								Show:
							</span>
							<Select
								value={liveInventoryData.limit.toString()}
								onValueChange={value =>
									setLiveInventoryData(prev => ({
										...prev,
										limit: Number(value),
										currentPage: 1,
									}))
								}
							>
								<SelectTrigger className="w-20">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="10">10</SelectItem>
									<SelectItem value="25">25</SelectItem>
									<SelectItem value="50">50</SelectItem>
									<SelectItem value="100">100</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{liveInventoryData.totalItems > 0 && (
							<div className="text-sm text-gray-600 dark:text-gray-400">
								Showing{" "}
								{Math.min(
									(liveInventoryData.currentPage - 1) *
									liveInventoryData.limit +
									1,
									liveInventoryData.totalItems,
								)}{" "}
								to{" "}
								{Math.min(
									liveInventoryData.currentPage * liveInventoryData.limit,
									liveInventoryData.totalItems,
								)}{" "}
								of {liveInventoryData.totalItems} results
							</div>
						)}
					</div>

					{/* Live Inventory Table */}
					<div className="rounded-md border">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="dark:text-gray-300">
										Product ID
									</TableHead>
									<TableHead className="dark:text-gray-300">
										Product Name
									</TableHead>
									<TableHead className="dark:text-gray-300">Pincode</TableHead>
									<TableHead className="dark:text-gray-300">
										Requested Qty
									</TableHead>
									<TableHead className="dark:text-gray-300">
										Current Stock
									</TableHead>
									<TableHead className="dark:text-gray-300">
										Request Date
									</TableHead>
									<TableHead className="dark:text-gray-300">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{liveInventoryData.isLoading ? (
									<TableRow>
										<TableCell
											colSpan={6}
											className="text-center dark:text-gray-400 py-8"
										>
											Loading missing product stock...
										</TableCell>
									</TableRow>
								) : liveInventoryData.items.length > 0 ? (
									liveInventoryData.items.map(item => (
										<TableRow
											key={item.id}
											className="hover:bg-gray-50 dark:hover:bg-gray-800"
										>
											<TableCell className="dark:text-gray-300">
												<span className="text-sm text-gray-700 dark:text-gray-300">
													{item.product_id}
												</span>
											</TableCell>
											<TableCell className="dark:text-gray-300 max-w-xs">
												<span
													className="text-sm text-gray-700 dark:text-gray-300 block truncate"
													title={item.product_name || "N/A"}
												>
													{item.product_name || "N/A"}
												</span>
											</TableCell>
											<TableCell className="dark:text-gray-300">
												{item.pincode || "N/A"}
											</TableCell>
											<TableCell className="dark:text-gray-300">
												{item.requested_quantity}
											</TableCell>
											<TableCell className="dark:text-gray-300">
												{item.current_stock !== null
													? item.current_stock
													: "N/A"}
											</TableCell>
											<TableCell className="dark:text-gray-300">
												{formatDate(new Date(item.created_at))}
											</TableCell>
											<TableCell>
												{!item.is_auto_stocked && item.tp_code && (
													<Button
														size="sm"
														onClick={() =>
															handleMarkAsAutoStock(item.product_id)
														}
														disabled={processingAutoStock === item.product_id}
														className="flex items-center gap-1"
													>
														{processingAutoStock === item.product_id ? (
															<>Processing...</>
														) : (
															<>
																<Check className="w-3 h-3" />
																Mark as Auto Stock
															</>
														)}
													</Button>
												)}
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell
											colSpan={6}
											className="text-center dark:text-gray-400 py-8"
										>
											No missing product stock found
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>

					{/* Pagination */}
					{liveInventoryData.totalPages > 1 && (
						<div className="flex justify-center">
							<Pagination
								page={liveInventoryData.currentPage}
								totalPages={liveInventoryData.totalPages}
								onPageChange={page =>
									setLiveInventoryData(prev => ({ ...prev, currentPage: page }))
								}
							/>
						</div>
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
};
